// ============================================================
// ExpressEnvStrategies.jsx
//
// A branded, responsive course content page for the
// "Multi-Environment Config & Deployment" lesson.
//
// Author: ProDev Architecture Series (DevOps Branch)
// Features: Environment-specific loading logic, validation
//           patterns, and navigation bridge to Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import LayersIcon            from "@mui/icons-material/Layers";
import DynamicFormIcon       from "@mui/icons-material/DynamicForm";
import RocketLaunchIcon      from "@mui/icons-material/RocketLaunch";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import ShieldIcon             from "@mui/icons-material/Shield";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressEnvStrategies() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Module 23"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Multi-Environment <span>Strategies</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        As your Express app scales across development, testing, staging, and production, managing environment-specific settings securely becomes essential. We use dynamic loading and a central config layer to keep deployments smooth.
                    </Typography>
                </Box>

                {/* 1. LAYERED ENV FILES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="1. Specific Environment Files" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Organize your settings into separate files based on the <code style={{color: BRAND.white}}>NODE_ENV</code> variable.
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            { file: ".env.development", desc: "Local database & debug keys." },
                            { file: ".env.production", desc: "Production secrets (Strictly ignored by Git)." },
                            { file: ".env.test", desc: "Mock APIs and test DB URIs for CI/CD." },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.file}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontFamily: "monospace", fontSize: 13, mb: 0.5 }}>{item.file}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 2. DYNAMIC LOADING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<DynamicFormIcon />} label="2. Conditional Dotenv Loading" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Dynamically resolve the path to your <code style={{color: BRAND.white}}>.env</code> file at runtime.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const dotenv = require('dotenv');", comment: "" },
                        { cmd: "const env = process.env.NODE_ENV || 'development';", comment: "Default to dev" },
                        { cmd: "", comment: "" },
                        { cmd: "dotenv.config({ path: `.env.${env}` });", comment: "Loads .env.production if NODE_ENV=production" },
                    ]} />
                </Box>

                {/* 3. CONFIG LAYER & VALIDATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="3. Centralized Config & Validation" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Map your variables to a central object and throw an error immediately if a critical secret is missing. This prevents the server from starting in a broken state.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// config/index.js", comment: "" },
                        { cmd: "if (!process.env.JWT_SECRET) {", comment: "Fail-fast validation" },
                        { cmd: "  throw new Error('FATAL: JWT_SECRET is not defined');", comment: "" },
                        { cmd: "}", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "module.exports = {", comment: "" },
                        { cmd: "  port: process.env.PORT || 3000,", comment: "" },
                        { cmd: "  dbUri: process.env.MONGO_URI,", comment: "" },
                        { cmd: "  isProd: process.env.NODE_ENV === 'production'", comment: "" },
                        { cmd: "};", comment: "" },
                    ]} />
                </Box>

                {/* 4. DEPLOYMENT TIPS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RocketLaunchIcon />} label="4. Production Deployment" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        In production, we typically don't use file-based environment variables.
                    </Typography>
                    <Grid container spacing={1.5}>
                        {[
                            "PaaS Injection: Use dashboards (Vercel, Heroku) for secret management.",
                            "Docker: Use --env-file at runtime or ENV instructions in Dockerfiles.",
                            "Secret Stores: Inject vars via AWS Secrets Manager or GitHub Actions.",
                            "Clean Git: Ensure all .env* variants are added to your .gitignore.",
                        ].map((item) => (
                            <Grid item xs={12} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> For robust validation, use a library like <code style={{ color: BRAND.goldBright }}>envalid</code> or <code style={{ color: BRAND.goldBright }}>joi</code> to validate your <code style={{ color: BRAND.goldBright }}>process.env</code> object. This ensures all required strings, ports, and URLs are properly formatted before the app logic even begins.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
